/**
 * Print asset group helpers, shared between the card designer's background
 * group UI and asset selection.
 */

/**
 * Finds the SYSTEM print asset group an asset image belongs to.
 *
 * @param {Array} groups `systemPrintAssetGroups` records (with members).
 * @param {?string} assetImageId The asset image id to look up.
 * @returns {?Object} The owning group, or null.
 */
export const getGroupForAssetImage = (groups, assetImageId) => {
  if (!assetImageId) {
    return null;
  }

  return (
    (groups ?? []).find(group =>
      (group.members?.records ?? []).some(
        member => member.print_asset?.asset_image?.id === assetImageId,
      ),
    ) ?? null
  );
};

/**
 * Builds the background element configuration for selecting a print asset:
 * the group link survives only when the newly selected asset belongs to the
 * currently linked group — anything else (another group's asset, an
 * ungrouped/user asset) removes the link.
 *
 * @param {Object} options The options.
 * @param {Object} options.printAsset The selected print asset.
 * @param {?string} options.currentGroupId The currently linked group id.
 * @param {Array} options.groups `systemPrintAssetGroups` records.
 * @returns {Object} The `photo_element_configuration` changes to apply.
 */
export const getBackgroundAssetElementConfig = ({
  printAsset,
  currentGroupId,
  groups,
}) => {
  const elementConfig = {
    existing_asset_image_id: printAsset.asset_image.id,
  };

  if (!currentGroupId) {
    return elementConfig;
  }

  const newGroup = getGroupForAssetImage(groups, printAsset.asset_image.id);

  if (newGroup?.id !== currentGroupId) {
    elementConfig.print_asset_group_id = null;
  }

  return elementConfig;
};

// Group names carry their orientation by convention: "Soccer (Portrait)".
const GROUP_ORIENTATION_SUFFIX = /\s*\((Portrait|Landscape)\)\s*$/;

/**
 * The orientation encoded in a group's name.
 *
 * @param {?Object} group The print asset group.
 * @returns {?string} An ORIENTATION value, or null when not encoded.
 */
export const getGroupOrientation = group => {
  const match = (group?.name ?? '').match(GROUP_ORIENTATION_SUFFIX);

  return match ? match[1].toUpperCase() : null;
};

/**
 * The group's display name — the raw name minus the orientation suffix.
 *
 * @param {?Object} group The print asset group.
 * @returns {string}
 */
export const getGroupDisplayName = group =>
  (group?.name ?? '').replace(GROUP_ORIENTATION_SUFFIX, '');

/**
 * The group's default variant: the primary member, first-available fallback.
 *
 * @param {?Object} group The print asset group (with members).
 * @returns {?Object} The default member, or null.
 */
export const getGroupDefaultMember = group => {
  const members = group?.members?.records ?? [];

  return members.find(member => member.is_primary) ?? members[0] ?? null;
};

/**
 * Whether a card has Smart Color Matching enabled: any of its sides carries a
 * photo element configuration linked to a print asset group.
 *
 * @param {?Object} card A card with `card_side_groups` and their sides'
 * `card_element_configurations`.
 * @returns {boolean}
 */
export const cardHasSmartColorMatching = card =>
  (card?.card_side_groups?.records ?? []).some(sideGroup =>
    (sideGroup.card_sides?.records ?? []).some(cardSide =>
      (cardSide.card_element_configurations ?? []).some(
        configuration => !!configuration?.print_asset_group?.id,
      ),
    ),
  );

/**
 * The Smart Color Matching explainer shown wherever a group can be toggled
 * (the canvas badge dropdown, the Waldo library group panel).
 *
 * @param {Object} options The options.
 * @param {Object} options.group The print asset group.
 * @param {boolean} options.smartEnabled Whether matching is currently on.
 * @returns {string}
 */
export const getGroupSmartMatchingCopy = ({ group, smartEnabled }) =>
  smartEnabled
    ? `Buyers automatically get the "${getGroupDisplayName(
        group,
      )}" variant matching their jersey color. Pick the variant to preview while designing:`
    : `Everyone gets the selected image. Enable matching to give each buyer the "${getGroupDisplayName(
        group,
      )}" variant for their jersey color.`;

/**
 * The group members matching an identity's jersey colors, using the shared
 * availability predicate: an untagged member is universal, a tagged member
 * must intersect the identity's colors. No identity colors means no matches.
 *
 * @param {?Object} group The print asset group (with members).
 * @param {Array} jerseyColors The identity's JerseyColorEnum values.
 * @returns {Array} The matching members.
 */
export const getGroupMembersForJerseyColors = (group, jerseyColors = []) => {
  if (jerseyColors.length === 0) {
    return [];
  }

  return (group?.members?.records ?? []).filter(member => {
    const colorRecords = member.print_asset?.jersey_colors?.records ?? [];

    return (
      colorRecords.length === 0 ||
      colorRecords.some(({ jersey_color: color }) =>
        jerseyColors.includes(color),
      )
    );
  });
};

/**
 * Whether a group belongs to any of the given pricing categories, judged by
 * its default (primary/first) member's print asset.
 *
 * @param {?Object} group The print asset group (with members).
 * @param {Array} pricingCategoryIds The pricing category ids to match.
 * @returns {boolean}
 */
export const groupMatchesPricingCategories = (group, pricingCategoryIds) => {
  const defaultMember = getGroupDefaultMember(group);
  const categoryRecords =
    defaultMember?.print_asset?.pricing_categories?.records ?? [];

  return categoryRecords.some(record =>
    pricingCategoryIds.includes(record.pricing_category?.id),
  );
};

/**
 * Builds the background element configuration for toggling Smart Color
 * Matching on a group. Enabling also applies the group's default variant
 * unless the current image already belongs to the group.
 *
 * @param {Object} options The options.
 * @param {Object} options.group The print asset group (with members).
 * @param {boolean} options.enable Whether matching is being turned on.
 * @param {?string} options.currentAssetImageId The background's asset image.
 * @returns {Object} The `photo_element_configuration` changes to apply.
 */
export const getGroupSmartToggleElementConfig = ({
  group,
  enable,
  currentAssetImageId,
}) => {
  if (!enable) {
    return { print_asset_group_id: null };
  }

  const elementConfig = { print_asset_group_id: group.id };
  const members = group.members?.records ?? [];
  const currentBelongsToGroup = members.some(
    member => member.print_asset?.asset_image?.id === currentAssetImageId,
  );

  if (!currentBelongsToGroup) {
    const defaultMember = getGroupDefaultMember(group);

    if (defaultMember?.print_asset?.asset_image?.id) {
      elementConfig.existing_asset_image_id =
        defaultMember.print_asset.asset_image.id;
    }
  }

  return elementConfig;
};
